/**
 * Smart Ratio Calculator
 *
 * ⚠️ CRITICAL: REVERSE CHRONOLOGICAL PROCESSING ⚠️
 * This calculator operates within the BACKWARDS PASS of revenue distribution.
 * When calculating for tax year 2050, years 2051+ have ALREADY been processed.
 * "Remaining" years means years we HAVEN'T processed yet (earlier years).
 *
 * Intelligent ratio calculation based on bond types (targeted vs unlimited).
 * Implements three distinct strategies:
 * 1. All Unlimited: Revenue capacity ratios
 * 2. All Targeted: Year-percentage of total revenue
 * 3. Mixed: Year-percentage for targeted, remainder for unlimited
 *
 * This ensures targeted bonds achieve their targets in mixed scenarios.
 */

import type { RevenueDistributionHelperMap } from '~/engine/processing/engines/distribution-engine/types';
import type { RevenueMap } from '~/types';
import { roundPrincipalDown, roundPrincipalUp } from '../core/financial-math/rounding';

export interface DistributionRatios {
  bondRatios: Record<string, number>; // Bond ID to ratio/amount mapping
  isAbsoluteAmount: boolean; // If true, values are amounts not percentages
  totalAllocated?: number; // Total amount allocated (for mixed scenarios)
  remainingForUnlimited?: number; // Remaining revenue for unlimited bonds
}

export interface BondCategorization {
  targetedBonds: string[];
  unlimitedBonds: string[];
  allBonds: string[];
}

export class SmartRatioCalculator {
  /**
   * Main entry point - calculates distribution ratios based on bond types
   */
  static calculateDistributionRatios(
    helperMap: RevenueDistributionHelperMap,
    revenueMap: RevenueMap,
    currentTaxYear: number,
    revenueAvailable: number
  ): DistributionRatios {
    // Categorize bonds
    const categorization = SmartRatioCalculator.categorizeBonds(helperMap);

    // Single bond - simple case
    if (categorization.allBonds.length === 1) {
      return SmartRatioCalculator.calculateSingleBondRatio(categorization.allBonds[0]);
    }

    // Multiple bonds - determine strategy based on types
    if (categorization.unlimitedBonds.length > 0 && categorization.targetedBonds.length === 0) {
      // All unlimited
      return SmartRatioCalculator.calculateUnlimitedRatios(
        categorization.unlimitedBonds,
        helperMap
      );
    } else if (
      categorization.targetedBonds.length > 0 &&
      categorization.unlimitedBonds.length === 0
    ) {
      // All targeted
      return SmartRatioCalculator.calculateTargetedRatios(
        categorization.targetedBonds,
        helperMap,
        revenueMap,
        currentTaxYear,
        revenueAvailable
      );
    } else {
      // Mixed scenario
      return SmartRatioCalculator.calculateMixedRatios(
        categorization.targetedBonds,
        categorization.unlimitedBonds,
        helperMap,
        revenueMap,
        currentTaxYear,
        revenueAvailable
      );
    }
  }

  /**
   * Categorizes bonds into targeted and unlimited
   */
  private static categorizeBonds(helperMap: RevenueDistributionHelperMap): BondCategorization {
    const targetedBonds: string[] = [];
    const unlimitedBonds: string[] = [];
    const allBonds: string[] = [];

    Object.entries(helperMap).forEach(([bondId, entry]) => {
      if (entry.isActive) {
        allBonds.push(bondId);
        if (entry.hasTarget) {
          targetedBonds.push(bondId);
        } else {
          unlimitedBonds.push(bondId);
        }
      }
    });

    return { targetedBonds, unlimitedBonds, allBonds };
  }

  /**
   * Single bond - gets all available revenue (up to target if targeted)
   */
  private static calculateSingleBondRatio(bondId: string): DistributionRatios {
    return {
      bondRatios: { [bondId]: 1.0 },
      isAbsoluteAmount: false,
    };
  }

  /**
   * All unlimited bonds - use revenue capacity ratios
   */
  private static calculateUnlimitedRatios(
    bondIds: string[],
    helperMap: RevenueDistributionHelperMap
  ): DistributionRatios {
    const ratios: Record<string, number> = {};
    const totalCapacity = bondIds.reduce(
      (sum, bondId) => sum + helperMap[bondId].minimumRevenueCapacity,
      0
    );

    if (totalCapacity > 0) {
      bondIds.forEach((bondId) => {
        const capacity = helperMap[bondId].minimumRevenueCapacity;
        ratios[bondId] = capacity / totalCapacity;
      });
    } else {
      // Equal distribution if no capacity info
      const equalRatio = 1 / bondIds.length;
      bondIds.forEach((bondId) => {
        ratios[bondId] = equalRatio;
      });
    }

    return {
      bondRatios: ratios,
      isAbsoluteAmount: false,
    };
  }

  /**
   * All targeted bonds - distribute full revenue based on remaining capacity ratios
   * When ALL bonds are targeted, we should use the full revenue available,
   * splitting it based on each bond's remaining target capacity
   */
  private static calculateTargetedRatios(
    bondIds: string[],
    helperMap: RevenueDistributionHelperMap,
    _revenueMap: RevenueMap,
    _currentTaxYear: number,
    revenueAvailable: number
  ): DistributionRatios {
    const allocations: Record<string, number> = {};

    // Calculate total remaining capacity across all targeted bonds
    let totalRemainingCapacity = 0;
    bondIds.forEach((bondId) => {
      const entry = helperMap[bondId];
      totalRemainingCapacity += entry.remainingTargetCapacity;
    });

    // Distribute the full revenue based on capacity ratios
    bondIds.forEach((bondId) => {
      const entry = helperMap[bondId];

      // Calculate this bond's share of the available revenue
      const capacityRatio =
        totalRemainingCapacity > 0
          ? entry.remainingTargetCapacity / totalRemainingCapacity
          : 1 / bondIds.length;

      let allocation = revenueAvailable * capacityRatio;

      // TODO: Investigate the impact of rounding up vs rounding down on distribution accuracy
      // Currently rounding up to ensure we maximize distribution - can always distribute less later
      // Round up to nearest increment for targeted bonds
      // Always round up for targeted bonds to ensure progress
      allocation = roundPrincipalUp(allocation);

      // Cap at remaining target capacity
      allocation = Math.min(allocation, entry.remainingTargetCapacity);

      allocations[bondId] = allocation;
    });

    return {
      bondRatios: allocations,
      isAbsoluteAmount: true,
      totalAllocated: Object.values(allocations).reduce((sum, val) => sum + val, 0),
    };
  }

  /**
   * Mixed scenario - targeted bonds get year-percentage allocation,
   * unlimited bonds share the remainder
   */
  private static calculateMixedRatios(
    targetedBondIds: string[],
    unlimitedBondIds: string[],
    helperMap: RevenueDistributionHelperMap,
    revenueMap: RevenueMap,
    currentTaxYear: number,
    revenueAvailable: number
  ): DistributionRatios {
    const allocations: Record<string, number> = {};

    // Step 1: Calculate allocations for targeted bonds
    let totalTargetedAllocation = 0;
    targetedBondIds.forEach((bondId) => {
      const entry = helperMap[bondId];
      const yearPercentage = SmartRatioCalculator.calculateYearPercentage(
        bondId,
        revenueMap,
        currentTaxYear,
        'remaining' // Always use remaining years (not yet processed in backwards pass)
      );

      // Calculate allocation with 5% buffer to ensure targets are met
      let allocation = yearPercentage * entry.remainingTargetCapacity;
      allocation *= 1.05; // 5% buffer for targeted allocations

      // Round up for targeted bonds
      // Always round up for targeted bonds to ensure progress
      allocation = roundPrincipalUp(allocation);

      // Cap at remaining target
      allocation = Math.min(allocation, entry.remainingTargetCapacity);
      allocations[bondId] = allocation;
      totalTargetedAllocation += allocation;
    });

    // Step 2: Calculate remaining revenue for unlimited bonds
    const remainingRevenue = Math.max(0, revenueAvailable - totalTargetedAllocation);

    // Step 3: Distribute remainder among unlimited bonds using capacity ratios
    if (remainingRevenue > 0 && unlimitedBondIds.length > 0) {
      const totalUnlimitedCapacity = unlimitedBondIds.reduce(
        (sum, bondId) => sum + helperMap[bondId].minimumRevenueCapacity,
        0
      );

      if (totalUnlimitedCapacity > 0) {
        unlimitedBondIds.forEach((bondId) => {
          const capacity = helperMap[bondId].minimumRevenueCapacity;
          const ratio = capacity / totalUnlimitedCapacity;
          allocations[bondId] = roundPrincipalDown(remainingRevenue * ratio);
        });
      } else {
        // Equal distribution if no capacity info
        const equalAmount = roundPrincipalDown(remainingRevenue / unlimitedBondIds.length);

        unlimitedBondIds.forEach((bondId) => {
          allocations[bondId] = equalAmount;
        });
      }
    }

    return {
      bondRatios: allocations,
      isAbsoluteAmount: true,
      totalAllocated: Object.values(allocations).reduce((sum, val) => sum + val, 0),
      remainingForUnlimited: remainingRevenue,
    };
  }

  /**
   * Calculates what percentage of a bond's total revenue opportunity
   * this year represents
   */
  private static calculateYearPercentage(
    bondId: string,
    revenueMap: RevenueMap,
    currentTaxYear: number,
    basis: 'remaining' | 'total'
  ): number {
    let currentYearCapacity = 0;
    let totalCapacity = 0;

    Object.entries(revenueMap).forEach(([_, revenueEntry]) => {
      const bondDetail = revenueEntry.bondSeriesDetailsByBondId[bondId];

      if (bondDetail?.isActive) {
        const capacity = revenueEntry.revenueCapacity > 0 ? revenueEntry.revenueCapacity : 0;

        if (revenueEntry.taxYear === currentTaxYear) {
          currentYearCapacity = capacity;
        }

        // Include in total based on basis setting
        if (basis === 'remaining') {
          if (revenueEntry.taxYear <= currentTaxYear) {
            totalCapacity += capacity;
          }
        } else {
          // 'total' - include all years where bond is active
          totalCapacity += capacity;
        }
      }
    });

    // Avoid division by zero
    if (totalCapacity === 0) {
      // If no capacity, assume equal distribution across remaining years
      const remainingYears = Object.values(revenueMap).filter((entry) => {
        const bondDetail = entry.bondSeriesDetailsByBondId[bondId];
        return bondDetail?.isActive && entry.taxYear <= currentTaxYear;
      }).length;

      return remainingYears > 0 ? 1 / remainingYears : 0;
    }

    const result = currentYearCapacity / totalCapacity;
    return result;
  }
}
